// features/admin/aboutApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Interfaces ───────────────────────────────────────────────────────────────

export interface AboutUs {
  id: number;
  heading: string;
  para1: string | null;
  para2: string | null;
  para3: string | null;
  is_active: number;
  created_at?: string;
  updated_at?: string;
}

export interface AboutApiResponse<T = any> {
  error: boolean;
  message?: string;
  total?: number;
  data?: T;
}

export interface AddAboutUsRequest {
  heading: string;
  para1?: string;
  para2?: string;
  para3?: string;
  is_active?: number;
}

export interface UpdateAboutUsRequest {
  id: number;
  heading?: string;
  para1?: string;
  para2?: string;
  para3?: string;
  is_active?: number;
}

export interface DeleteAboutUsRequest {
  id: number;
}

// ─── API Slice ────────────────────────────────────────────────────────────────

export const aboutApi = createApi({
  reducerPath: 'aboutApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT || 'https://fftestservice.sarsetech.com',
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      headers.set('Content-Type', 'application/x-www-form-urlencoded');
      return headers;
    },
  }),
  tagTypes: ['AboutUs'],
  endpoints: (build) => ({

    // ── List (GET — no auth on backend) ───────────────────────────────────────
    listAboutUs: build.query<AboutApiResponse<AboutUs[]>, void>({
      query: () => ({
        url:    '/globalsettings/listAboutUs',
        method: 'GET',
      }),
      providesTags: ['AboutUs'],
      transformResponse: (response: any): AboutApiResponse<AboutUs[]> => ({
        error:   response?.error ?? false,
        message: response?.message || '',
        total:   response?.total  ?? 0,
        data:    response?.data   || [],
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to load About Us records', data: [],
      }),
    }),

    // ── Add ───────────────────────────────────────────────────────────────────
    addAboutUs: build.mutation<AboutApiResponse, AddAboutUsRequest>({
      query: (body) => {
        const formData = new URLSearchParams();
        formData.append('heading', body.heading);
        if (body.para1     !== undefined) formData.append('para1',     body.para1);
        if (body.para2     !== undefined) formData.append('para2',     body.para2);
        if (body.para3     !== undefined) formData.append('para3',     body.para3);
        if (body.is_active !== undefined) formData.append('is_active', String(body.is_active));

        return {
          url:    '/globalsettings/addAboutUs',
          method: 'POST',
          body:   formData.toString(),
        };
      },
      invalidatesTags: ['AboutUs'],
      transformResponse: (response: any): AboutApiResponse => ({
        error:   response?.error ?? false,
        message: response?.message || 'Added successfully',
        data:    response?.id,
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to add About Us',
      }),
    }),

    // ── Update ────────────────────────────────────────────────────────────────
    updateAboutUs: build.mutation<AboutApiResponse, UpdateAboutUsRequest>({
      query: (body) => {
        const formData = new URLSearchParams();
        formData.append('id', String(body.id));
        if (body.heading   !== undefined) formData.append('heading',   body.heading);
        if (body.para1     !== undefined) formData.append('para1',     body.para1);
        if (body.para2     !== undefined) formData.append('para2',     body.para2);
        if (body.para3     !== undefined) formData.append('para3',     body.para3);
        if (body.is_active !== undefined) formData.append('is_active', String(body.is_active));

        return {
          url:    '/globalsettings/updateAboutUs',
          method: 'POST',
          body:   formData.toString(),
        };
      },
      invalidatesTags: ['AboutUs'],
      transformResponse: (response: any): AboutApiResponse => ({
        error:   response?.error ?? false,
        message: response?.message || 'Updated successfully',
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to update About Us',
      }),
    }),

    // ── Delete ────────────────────────────────────────────────────────────────
    deleteAboutUs: build.mutation<AboutApiResponse, DeleteAboutUsRequest>({
      query: (body) => ({
        url:    '/globalsettings/deleteAboutUs',
        method: 'POST',
        body:   new URLSearchParams({ id: String(body.id) }).toString(),
      }),
      invalidatesTags: ['AboutUs'],
      transformResponse: (response: any): AboutApiResponse => ({
        error:   response?.error ?? false,
        message: response?.message || 'Deleted successfully',
      }),
      transformErrorResponse: (response: any) => ({
        error: true, message: response.data?.message || 'Failed to delete About Us',
      }),
    }),

  }),
});

export const {
  useListAboutUsQuery,
  useAddAboutUsMutation,
  useUpdateAboutUsMutation,
  useDeleteAboutUsMutation,
} = aboutApi;